// Angular
import {
  Component,
  Input,
  ViewEncapsulation,
  AfterViewChecked,
  Renderer2,
  ElementRef,
} from '@angular/core';

// Third-party
import { Node } from '@alfresco/js-api';

// Alfresco
import {
  AppExtensionService,
  NodePermissionService,
} from '@alfresco/aca-shared';

// Project
import { WkAppConfigService } from '../../../services';
import { MetadataTabData } from '../card-view/interfaces/card-view-table-item-properties.interface';

@Component({
  selector: 'wk-metadata-tab',
  template: `
    <wk-content-metadata-card
      [readOnly]="!canUpdateNode"
      [preset]="preset"
      [node]="node"
      [isEditMode]="isEditMode"
      [displayDefaultProperties]="displayDefaultProperties"
    >
    </wk-content-metadata-card>
  `,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'wk-metadata-tab' },
  standalone: false,
})
export class MetadataTabComponent implements AfterViewChecked {
  private _node: Node;
  @Input()
  set node(node: Node) {
    this._node = node;
    this.setPreset();
    this.checkMetadataConfig();
  }
  get node() {
    return this._node;
  }
  @Input()
  isEditMode: boolean;
  displayDefaultProperties: boolean;
  preset: string;
  externalMetadataPrefix = 'externals:';
  fakeExternalAspect = {
    id: 'app.content.metadata.externalAspect',
    aspect: 'externals:externals',
    properties: '*',
  };

  public set data({ displayNode, isEditMode }: MetadataTabData) {
    this.node = displayNode;
    this.isEditMode = isEditMode;
  }

  constructor(
    private readonly permission: NodePermissionService,
    protected extensions: AppExtensionService,
    private readonly appConfig: WkAppConfigService,
    protected renderer: Renderer2,
    protected elementRef: ElementRef
  ) {
    try {
      this.appConfig.config['content-metadata'] =
        this.extensions.contentMetadata;
      this.displayDefaultProperties = !this.appConfig.get(
        'externalMetadataApi.isActive'
      );
    } catch (error) {
      console.error(
        error,
        '- could not change content-metadata from app.config'
      );
    }
  }

  private setPreset(): void {
    const nodeMapping = this.appConfig.get('nodeType-metadata-mapping');
    this.preset =
      nodeMapping[this._node.nodeType]?.preset || nodeMapping['default'].preset;
  }

  private checkExternalPrefix(presetItem: any): boolean {
    return (
      (presetItem.type &&
        presetItem.type.startsWith(this.externalMetadataPrefix)) ||
      (presetItem.aspect &&
        presetItem.aspect.startsWith(this.externalMetadataPrefix))
    );
  }

  private checkMetadataConfig(): void {
    if (
      !this.appConfig.get('externalMetadataApi.isActive') ||
      !this.node.nodeType
    ) {
      return;
    }

    let hasExternalMetadata = false;
    const customGroup = this.extensions.contentMetadata.presets.custom[0];
    const newConfig = [];
    const configItems = customGroup.items;

    configItems.forEach((configItem: any) => {
      if (this.checkExternalPrefix(configItem)) {
        hasExternalMetadata = true;
      } else {
        if (
          configItem.id &&
          configItem.id === 'app.content.metadata.customType'
        ) {
          configItem.type = this.node.nodeType;
        }
        newConfig.push(configItem);
      }
    });

    if (hasExternalMetadata && this.node.aspectNames) {
      // fake aspect to keep external config between types
      newConfig.push(this.fakeExternalAspect);
      this.node.aspectNames.forEach((aspectName: string, index: number) => {
        if (aspectName.startsWith(this.externalMetadataPrefix)) {
          newConfig.push({
            aspect: aspectName,
            id: `app.content.metadata.externalAspect${index}`,
            properties: '*',
          });
        }
      });
    }

    customGroup.items = newConfig;
  }

  get canUpdateNode(): boolean {
    if (this.node && this.node.isFolder) {
      return this.permission.check(this.node, ['update']);
    }

    if (this.node && this.node.isFile && !this.isFileLocked()) {
      return this.permission.check(this.node, ['update']);
    }

    return false;
  }

  private isFileLocked(): boolean {
    return (
      this.node.isLocked ||
      (this.node.properties &&
        this.node.properties['cm:lockType'] === 'READ_ONLY_LOCK')
    );
  }

  ngAfterViewChecked(): void {
    const metadataCardItems = Array.from(
      this.elementRef.nativeElement.querySelectorAll('.adf-property-value')
    );
    metadataCardItems.forEach((cardItem: any) => {
      this.renderer.setAttribute(cardItem, 'title', cardItem.innerText);
    });
  }
}
